"use client";

import React from "react";
import { IDigitalChannelsBlock } from "@/page-builder/fragments/DigitalChannelsBlock/types";
import { motion } from "framer-motion";
import Heading from "@/components/molecules/Heading";
import BupiLinkButton from "@/components/atoms/linkButton";
import Image from "@/components/atoms/image";
import WrapperBlueStyle from "@/components/atoms/wrapper-blue-style";
import { useTranslations } from "next-intl";
import { convertLexicalWithInternalLinks } from "@/lib/lexical";
import styles from "./DigitalChannelsBlock.module.css";
import { getAccessibleImageProps } from "@/lib/image-alt";

export const DigitalChannelsBlock: React.FC<IDigitalChannelsBlock> = ({
  apps,
  background
}) => {
  const t = useTranslations("DigitalChannelsBlock");

  const Content = (
    <section
      className={` text-[#2B363C] space-y-32 md:space-y-64 ${
        background !== "default" ? styles["land-registration-guide"] + " bg-primary-100 mb-[-90px] pt-64 md:pt-96 pb-[150px] md:pb-[200px]" : " py-64 md:py-96"
      }`}
    >
      {apps?.map((info, index) => (
        <div key={index} className="mx-auto container flex flex-col gap-32">
          <div className="grid grid-cols-1 xl:grid-cols-8 md:items-center gap-32">
            <div
              className={`relative flex col-span-4 justify-end ${
                info?.position === "right" ? "order-1 xl:order-2" : "order-1"
              }`}
            >
              <motion.img
                src={info?.image?.url || ""}
                {...getAccessibleImageProps(info?.image?.alt)}
                className="w-full max-w-[296px] md:max-w-[468px] xl:max-w-[592px] h-auto z-0 mx-auto"
                initial={{
                  opacity: 0,
                  x: info?.position === "right" ? 80 : -80,
                }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  x: { duration: 2, ease: "easeOut" },
                  opacity: { duration: 1.5 },
                }}
              />
            </div>
            <div
              className={`flex col-span-4 flex-col justify-center space-y-8 ${
                info?.position === "right" ? "order-2 xl:order-1" : "order-2"
              }`}
            >
              <Heading title={info?.title} />
              <div
                dangerouslySetInnerHTML={{
                  __html: convertLexicalWithInternalLinks(info?.description as any),
                }}
                className="text-l-regular py-8 prose prose-ul:my-0 marker:text-neutral-900"
              />
              {info.access &&
                (info.access.appleStoreURL || info.access.googlePlayStoreURL) &&
                !info.access.title &&
                !info.access.description && (
                  <div className="flex flex-col md:flex-row gap-24 xl:gap-32 items-start flex-wrap">
                    {info.access.googlePlayStoreURL && (
                      <a
                        href={info.access.googlePlayStoreURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-[60px] md:max-w-[202px]"
                      >
                        <div className="w-full h-full reltative flex items-center justify-center">
                          <Image
                            width={202}
                            height={60}
                            src="/assets/app/google-play.svg"
                            alt={t("info")}
                            className="object-contain"
                          />
                        </div>
                      </a>
                    )}
                    {info.access.appleStoreURL && (
                      <a
                        href={info.access.appleStoreURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-[60px] md:max-w-[202px]"
                      >
                        <div className="w-full h-full reltative flex items-center justify-center">
                          <Image
                            width={202}
                            height={60}
                            src="/assets/app/app-store.svg"
                            alt={t("info2")}
                            className="object-contain"
                          />
                        </div>
                      </a>
                    )}
                  </div>
                )}
            </div>
          </div>

          {(info?.guide || info?.access) && 
          <div className="grid grid-cols-1 md:grid-cols-2 gap-32 ">
            {info?.guide && info.guide.title && info.guide.description && (
              <div className="space-y-16 flex flex-col items-start">
                <h2 className="text-l-bold md:text-xl-bold xl:text-2xl-bold">
                  {info.guide.title}
                </h2>
                <p className="text-m-regular">{info.guide.description}</p>
                {info.guide.ctaButton && (
                  <BupiLinkButton
                    label={info.guide.ctaButton.label}
                    link={info.guide.ctaButton.url}
                    target={info.guide.ctaButton.target}
                    hasIcon
                    leadingIcon="agora-line-arrow-right-circle"
                  />
                )}
              </div>
            )}

            {info?.access && info.access.title && info.access.description && (
              <div className="space-y-16 xl:col-start-2 flex flex-col items-start w-full">
                <h2 className="text-l-bold md:text-xl-bold xl:text-2xl-bold">
                  {info.access.title}
                </h2>
                <p className="text-m-regular">{info.access.description}</p>

                {info.access.ctaButton && (
                  <BupiLinkButton
                    label={info.access.ctaButton.label}
                    link={info.access.ctaButton.url}
                    target={info.access.ctaButton.target}
                    hasIcon
                    leadingIcon="agora-line-arrow-right-circle"
                    className=""
                  />
                )}

                {(info.access.appleStoreURL || info.access.googlePlayStoreURL) && (
                  <div className="flex flex-col md:flex-row gap-24 xl:gap-32 items-start flex-wrap w-full">
                    {info.access.googlePlayStoreURL && (
                      <a
                        href={info.access.googlePlayStoreURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-[60px] md:max-w-[202px]"
                      >
                        <div className="w-full h-full reltative flex items-center justify-center">
                          <Image
                            width={202}
                            height={60}
                            src="/assets/app/google-play.svg"
                            alt={t("info")}
                            className="object-contain"
                          />
                        </div>
                      </a>
                    )}
                    {info.access.appleStoreURL && (
                      <a
                        href={info.access.appleStoreURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        //className="border-2 border-[#A7A6A6] bg-black text-white rounded-8 hover:bg-black/90 w-full h-[60px] md:max-w-[202px]"
                      >
                        <div className="w-full h-full reltative flex items-center justify-center">
                          <Image
                            width={202}
                            height={60}
                            src="/assets/app/app-store.svg"
                            alt={t("info2")}
                            className="object-contain"
                          />
                        </div>
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          }
        </div>
      ))}
    </section>
  );

  return background === "default" ? <WrapperBlueStyle>{Content}</WrapperBlueStyle> : Content;
};
